import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ForgotPasswordComponent } from './no-auth/forgot-password/forgot-password.component';
import { LoginComponent } from './no-auth/login/login.component';
import { LayoutComponent } from './auth/layout/layout.component';
import { UsersComponent } from './auth/users/users.component';
import { TransactionsComponent } from './auth/transactions/transactions.component';
import { ReconciliationComponent } from './auth/reconciliation/reconciliation.component';
import { CountryComponent } from './auth/country/country.component';
import { CurrencyComponent } from './auth/currency/currency.component';
import { CustomersComponent } from './auth/customers/customers.component';
import { CustomerDetailsComponent } from './auth/customers/customer-details/customer-details.component';
import { BeneficiaryTransactionsComponent } from './auth/customers/beneficiary-transactions/beneficiary-transactions.component';
import { RateConversionComponent } from './auth/rate-conversion/rate-conversion.component';
import { LedgersComponent } from './auth/ledgers/ledgers.component';
import { DashboardComponent } from './auth/dashboard/dashboard.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { SecurityQuestionsComponent } from './auth/settings/security-questions/security-questions.component';
import { MfaComponent } from './auth/settings/mfa/mfa.component';
import { MfaLoginComponent } from './no-auth/mfa-login/mfa-login.component';
import { authGuard } from './shared/helpers/auth.guard';
import { BeneficiariesComponent } from './auth/beneficiaries/beneficiaries.component';
import { SettlementComponent } from './auth/settlement/settlement.component';

/**
 * Partner role vocabulary.
 *
 * These MUST be the roles a partner user can actually hold. The backend
 * `partnerUsers` schema pins that set: `enum: ["Admin", "Manager", "Staff"]`.
 *
 * The lists here previously read `['admin', 'super admin', 'executive',
 * 'manager', 'support']` — the PLATFORM (back-office) role names, which no
 * partner user has, and which omit `staff` entirely. That went unnoticed because
 * the guard never ran against these routes (see the parent route below); the
 * moment it did, every Staff user would have been bounced off every screen,
 * including the `/app/dashboard` the guard redirects to.
 */
const PARTNER_ROLES = ['admin', 'manager', 'staff'];

/**
 * Partner user administration.
 *
 * Mirrors the backend authority for these screens — `_util.isPartnerUserAdmin`
 * admits partner `admin` and `manager`. The previous `['admin', 'super admin']`
 * contradicted it: it would have blocked a Manager from a screen the API grants
 * them, while listing a role no partner can hold.
 */
const PARTNER_ADMIN_ROLES = ['admin', 'manager'];

export const routes: Routes = [
  { path: '', component: LoginComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'mfa-login', component: MfaLoginComponent, canActivate: [authGuard], data: { skipMfaCheck: true } },
  {
    /**
     * `canActivateChild` is the fix for AUTHZ-005.
     *
     * `canActivate` on this parent route receives the PARENT's
     * ActivatedRouteSnapshot, whose `data` is `{}` — the `roles` below live on
     * the children and Angular does not hand them upward. `authGuard` therefore
     * found `requiredRoles === undefined` and skipped its role branch on every
     * navigation, so `data: { roles: ... }` was decorative. It also only ran when
     * `/app` was first entered, not when moving between children, so typing a
     * restricted URL after login was never checked at all.
     *
     * `canActivateChild` runs the SAME guard against the child snapshot being
     * activated, on every child navigation, so `route.data['roles']` is the
     * child's own metadata. `canActivate` is kept so the authentication and MFA
     * branches still gate entry into `/app` itself.
     */
    path: 'app', component: LayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      { path: '', component: DashboardComponent, data: { roles: PARTNER_ROLES } },
      { path: 'dashboard', component: DashboardComponent, data: { roles: PARTNER_ROLES } },
      { path: 'users', component: UsersComponent, data: { roles: PARTNER_ADMIN_ROLES } },
      { path: 'reconciliation', component: ReconciliationComponent, data: { roles: PARTNER_ROLES } },
      { path: 'transactions', component: TransactionsComponent, data: { roles: PARTNER_ROLES } },
      { path: 'country', component: CountryComponent, data: { roles: PARTNER_ROLES } },
      { path: 'currency', component: CurrencyComponent, data: { roles: PARTNER_ROLES } },
      { path: 'customers', component: CustomersComponent, data: { roles: PARTNER_ROLES } },
      { path: 'customers/details', component: CustomerDetailsComponent, data: { roles: PARTNER_ROLES } },
      { path: 'customers/beneficiary-transactions', component: BeneficiaryTransactionsComponent, data: { roles: PARTNER_ROLES } },
      { path: 'rate-conversion', component: RateConversionComponent, data: { roles: PARTNER_ROLES } },
      { path: 'ledgers', component: LedgersComponent, data: { roles: PARTNER_ROLES } },
      { path: 'reset-password', component: ResetPasswordComponent, data: { roles: PARTNER_ROLES } },
      { path: 'security-questions', component: SecurityQuestionsComponent, data: { roles: PARTNER_ROLES } },
      { path: 'mfa', component: MfaComponent, data: { roles: PARTNER_ROLES } },
      { path: 'beneficiaries', component: BeneficiariesComponent, data: { roles: PARTNER_ROLES } },
      { path: 'settlement', component: SettlementComponent, data: { roles: PARTNER_ROLES } },
    ]
  }
];
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {

}